import type { ClientInvoiceItemDto } from '../../lib/clientInvoiceApi';
import { num } from '../../utils/invoiceLineMath';

/**
 * D157.9: where a client-invoice line's cost came from, in the owner's words. Derived from the stored
 * `costBasis` when read, never stored. Staff only: the line drawer shows it, and the lines list's
 * cost-source tag and its colour (D171.8, D172.1); never the CSV, a PDF, the discrepancy warning or alertHistory.
 */
const BASIS_LABELS: Record<string, string> = {
  po: 'PO price (no supplier bill yet)', // 29 Oct call, Deborah (D172.2): the old wording was not understood
  billed: "From the supplier's bill (via its PO)",
  shipped: 'From stock sent',
  manual: 'Typed by hand',
  chosen: 'Set on this line',
};

/** null when the line states no cost, or a hand line has no basis. */
export function costSourceLabel(
  item: Pick<ClientInvoiceItemDto, 'costBasis' | 'actualUnitCost' | 'confirmedOrderItemIndex' | 'billedFrom'>
): string | null {
  if (num(item.actualUnitCost) == null) return null;
  // A cost that stands on a confirmed bill names it when it is known ("From vendor invoice WIT-TEST-CASE12-001").
  if (item.costBasis === 'billed' && item.billedFrom && item.billedFrom.trim()) return `From vendor invoice ${item.billedFrom.trim()}`;
  if (item.costBasis) return BASIS_LABELS[item.costBasis] ?? null;
  return item.confirmedOrderItemIndex != null ? "The order's own cost" : null;
}

type CostSourceItem = Parameters<typeof costSourceLabel>[0];

/**
 * 29 Oct call, Deborah (D172.1): red = the cost is still the quoted/estimated one, green = the supplier's real
 * cost. Derived when read, never stored. The text tag rides with the colour so colour is never the only signal;
 * 'bill' is the D171.8 tag. null = no tone (no cost, a hand line with no basis, an unknown basis).
 */
export type CostSourceTone = { tone: 'positive' | 'negative' | 'neutral'; tag: string };
const BASIS_TONES: Record<string, CostSourceTone> = {
  billed: { tone: 'positive', tag: 'bill' },
  shipped: { tone: 'positive', tag: 'stock' },
  chosen: { tone: 'positive', tag: 'set' },
  po: { tone: 'negative', tag: 'PO' },
  manual: { tone: 'neutral', tag: 'typed' },
};

export function costSourceTone(item: CostSourceItem): CostSourceTone | null {
  if (num(item.actualUnitCost) == null) return null;
  if (item.costBasis) return BASIS_TONES[item.costBasis] ?? null;
  return item.confirmedOrderItemIndex != null ? { tone: 'negative', tag: 'quote' } : null;
}

/** Beside every way to take an unpaired supplier bill's price (the Vendor Bill suggestion rows, Import Vendor Bill). */
export const SET_ON_THIS_LINE_NOTE = 'Sets the cost on this line only; it does not pair the bill with a PO.';
